import React, { useState, useMemo, useCallback } from 'react'
import { Card, Button, Typography } from 'antd'
import { useSelectedImage } from '../hooks/useSelectedImage'
import { useExamplePhotos } from '../hooks/useExamplePhotos'
import { useSensorTracking } from '../hooks/useSensorTracking'
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  SyncOutlined
} from '@ant-design/icons'

// 上一页按钮组件
interface XCNavigationButtonProps {
  direction: 'prev' | 'next'
  onClick: () => void
}

const NavigationButton: React.FC<XCNavigationButtonProps> = React.memo(
  ({ direction, onClick }) => {
    const isPrev = direction === 'prev'

    return (
      <Button
        style={{
          width: '37px',
          height: '110px',
          backgroundColor: '#F5F5F5',
          border: 'none',
          borderRadius: '7px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={onClick}
      >
        {isPrev ? (
          <CaretLeftOutlined style={{ color: '#999999', fontSize: '24px' }} />
        ) : (
          <CaretRightOutlined style={{ color: '#999999', fontSize: '24px' }} />
        )}
        <span
          style={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#999999',
            lineHeight: '1.17',
            writingMode: 'vertical-rl',
            textOrientation: 'upright',
            marginRight: '8px'
          }}
        >
          {isPrev ? '上一页' : '下一页'}
        </span>
      </Button>
    )
  }
)

/**
 * 示例照片区域组件
 */
const ExamplePhotos: React.FC = React.memo(() => {
  const [currentPage, setCurrentPage] = useState(0)
  const { addFromExample } = useSelectedImage()
  const { reportAISafetyHazardsClick } = useSensorTracking()

  // 每页显示的照片数量
  const photosPerPage = 7
  const { photos, refreshPhotos } = useExamplePhotos(photosPerPage)

  const totalPages = Math.ceil(photos.length / photosPerPage)

  // 当前页的照片
  const currentPhotos = useMemo(() => {
    const startIndex = currentPage * photosPerPage
    return photos.slice(startIndex, startIndex + photosPerPage)
  }, [photos, currentPage, photosPerPage])

  /**
   * 处理换一批
   */
  const handleShuffle = useCallback(async () => {
    // 上报换一批埋点
    reportAISafetyHazardsClick({ clickItem: 'switchPic' })

    try {
      // 刷新照片数据
      await refreshPhotos()
      // 重置到第一页
      setCurrentPage(0)
    } catch (err) {
      console.error('刷新示例照片失败:', err)
      // 如果刷新失败，随机跳转到其他页面
      const randomPage = Math.floor(Math.random() * totalPages)
      setCurrentPage(randomPage)
    }
  }, [refreshPhotos, totalPages, reportAISafetyHazardsClick])

  /**
   * 处理示例图片选择
   */
  const handleSelectExample = useCallback(
    (photo: string) => {
      // 上报点击示例照片埋点
      reportAISafetyHazardsClick({ clickItem: 'clickOneExamplePhoto' })
      addFromExample(photo)
    },
    [addFromExample, reportAISafetyHazardsClick]
  )

  // 如果获取的示例照片为空，则不展示该组件
  if (photos.length === 0) {
    return null
  }

  return (
    <div
      style={{
        width: '100%'
      }}
    >
      {/* 标题和换一批按钮 - 水平分布 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          marginBottom: '16px',
          paddingLeft: '30px'
        }}
      >
        <Typography
          style={{
            fontSize: '24px',
            color: '#333',
            fontWeight: 'bold'
          }}
        >
          示例照片
        </Typography>
        <Button
          style={{
            width: '103px',
            height: '38px',
            backgroundColor: '#F5F5F5',
            border: 'none',
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            cursor: 'pointer',
            marginLeft: '12px'
          }}
          onClick={handleShuffle}
        >
          <SyncOutlined
            style={{
              fontSize: '20px',
              color: '#999999'
            }}
          />
          <span
            style={{
              fontSize: '18px',
              fontWeight: 500,
              color: '#848484'
            }}
          >
            换一批
          </span>
        </Button>
      </div>

      {/* 主容器区域 - 一行展示 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '25px',
          minHeight: '159px',
          paddingLeft: '30px',
          paddingRight: '30px'
        }}
      >
        {/* 上一页按钮 */}
        <NavigationButton direction="prev" onClick={handleShuffle} />

        {/* 主容器 */}
        <Card
          style={{
            flex: 1,
            minHeight: '127px',
            borderRadius: '10px',
            boxShadow: '-2px -1px 13.6px 0px rgba(233, 233, 233, 1)'
          }}
          styles={{ body: { padding: '16px' } }}
        >
          {/* 照片网格 */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '95px',
              width: '100%'
            }}
          >
            {currentPhotos.map((photo: string) => (
              <div
                key={photo}
                style={{
                  flex: '1 1 calc((100% - 48px) / 7)', // 7张图片，6个间隙，每个间隙8px
                  aspectRatio: '1',
                  maxWidth: 'calc((100% - 48px) / 7)',
                  minWidth: '80px',
                  borderRadius: '7px',
                  overflow: 'hidden',
                  backgroundColor: '#D9D9D9',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
                onClick={() => handleSelectExample(photo)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                }}
              >
                <img
                  src={photo}
                  alt={photo}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* 下一页按钮 */}
        <NavigationButton direction="next" onClick={handleShuffle} />
      </div>
    </div>
  )
})

export default ExamplePhotos
